import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Mail, RefreshCw, Trash2, ArrowRight, Plus, X } from 'lucide-react';
import DOMPurify from 'dompurify';

export default function MailInbox() {
  const [mails, setMails] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [ownerType, setOwnerType] = useState({});
  const [selectedSupplier, setSelectedSupplier] = useState({});
  const [selectedSupplierContact, setSelectedSupplierContact] = useState({});
  const [supplierContacts, setSupplierContacts] = useState({});
  const [contacts, setContacts] = useState({});
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [converting, setConverting] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState({});
  const [selectedContact, setSelectedContact] = useState({});
  const [expandedMail, setExpandedMail] = useState(null);

  // Modals
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [showNewContact, setShowNewContact] = useState(false);
  const [newContactMailId, setNewContactMailId] = useState(null);
  const [customerForm, setCustomerForm] = useState({ name: '', customer_number: '' });
  const [contactForm, setContactForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });

  const load = async () => {
    try {
      const [mailsRes, customersRes, suppliersRes] = await Promise.all([
        api.get('/mail/inbox'),
        api.get('/customers'),
        api.get('/suppliers'),
      ]);
      setMails(mailsRes.data);
      setCustomers(customersRes.data.filter(c => c.is_active));
      setSuppliers((suppliersRes.data.suppliers || []).filter(s => s.is_active));
    } catch (err) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const loadSupplierContacts = async (mailId, supplierId) => {
    setSelectedSupplier(prev => ({ ...prev, [mailId]: supplierId }));
    setSelectedSupplierContact(prev => ({ ...prev, [mailId]: '' }));
    if (!supplierId) { setSupplierContacts(prev => ({ ...prev, [mailId]: [] })); return; }
    try {
      const res = await api.get('/supplier-contacts/supplier/' + supplierId);
      setSupplierContacts(prev => ({ ...prev, [mailId]: res.data }));
    } catch (e) {}
  };

  const loadContacts = async (mailId, customerId) => {
    setSelectedCustomer(prev => ({ ...prev, [mailId]: customerId }));
    setSelectedContact(prev => ({ ...prev, [mailId]: '' }));
    if (!customerId) { setContacts(prev => ({ ...prev, [mailId]: [] })); return; }
    try {
      const res = await api.get('/contacts/customer/' + customerId);
      setContacts(prev => ({ ...prev, [mailId]: res.data.filter(c => c.is_active) }));
    } catch (err) {}
  };

  const handleFetch = async () => {
    setFetching(true);
    try {
      const res = await api.post('/mail/fetch');
      toast.success(res.data.message);
      load();
    } catch (err) {
      toast.error('Fehler beim Abholen');
    } finally {
      setFetching(false);
    }
  };

  const handleConvert = async (mailId) => {
    const type = ownerType[mailId] || 'customer';
    const customerId = selectedCustomer[mailId];
    const supplierId = selectedSupplier[mailId];
    if (type === 'customer' && !customerId) { toast.error('Bitte einen Kunden auswaehlen'); return; }
    if (type === 'supplier' && !supplierId) { toast.error('Bitte einen Lieferanten auswaehlen'); return; }
    setConverting(mailId);
    try {
      await api.post('/mail/inbox/' + mailId + '/convert', {
        customer_id: type === 'customer' ? parseInt(customerId) : null,
        contact_id: type === 'customer' && selectedContact[mailId] ? parseInt(selectedContact[mailId]) : null,
        supplier_id: type === 'supplier' ? parseInt(supplierId) : null,
        supplier_contact_id: type === 'supplier' && selectedSupplierContact[mailId] ? parseInt(selectedSupplierContact[mailId]) : null,
      });
      toast.success('Ticket erstellt');
      load();
    } catch (err) {
      toast.error('Fehler beim Erstellen');
    } finally {
      setConverting(null);
    }
  };

  const handleDelete = async (mailId) => {
    try {
      await api.delete('/mail/inbox/' + mailId);
      toast.success('Mail ignoriert');
      load();
    } catch (err) {
      toast.error('Fehler');
    }
  };

  const handleCreateCustomer = async () => {
    if (!customerForm.name || !customerForm.customer_number) {
      toast.error('Name und Kundennummer sind erforderlich');
      return;
    }
    try {
      const res = await api.post('/customers', customerForm);
      toast.success('Kunde angelegt');
      setShowNewCustomer(false);
      setCustomerForm({ name: '', customer_number: '' });
      await load();
      // Neuen Kunden in allen offenen Mails vorselektieren
      if (expandedMail) {
        loadContacts(expandedMail, res.data.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Fehler beim Anlegen');
    }
  };

  const handleCreateContact = async () => {
    if (!contactForm.first_name || !contactForm.last_name) {
      toast.error('Vor- und Nachname sind erforderlich');
      return;
    }
    const customerId = selectedCustomer[newContactMailId];
    if (!customerId) { toast.error('Bitte zuerst einen Kunden auswaehlen'); return; }
    try {
      const res = await api.post('/contacts', { ...contactForm, customer_id: parseInt(customerId) });
      toast.success('Ansprechpartner angelegt');
      setShowNewContact(false);
      setContactForm({ first_name: '', last_name: '', email: '', phone: '' });
      const contactsRes = await api.get('/contacts/customer/' + customerId);
      setContacts(prev => ({ ...prev, [newContactMailId]: contactsRes.data.filter(c => c.is_active) }));
      setSelectedContact(prev => ({ ...prev, [newContactMailId]: res.data.id }));
    } catch (err) {
      toast.error('Fehler beim Anlegen');
    }
  };

  if (loading) return <div className="text-gray-400 text-sm">Laden...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Posteingang</h2>
          <p className="text-gray-500 text-sm mt-1">Unbekannte Absender – manuell zuordnen</p>
        </div>
        <button onClick={handleFetch} disabled={fetching}
          className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-sm disabled:opacity-50">
          <RefreshCw size={15} className={fetching ? 'animate-spin' : ''} />
          {fetching ? 'Wird abgeholt...' : 'Mails abholen'}
        </button>
      </div>

      {mails.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
          <Mail size={32} className="text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Keine unverarbeiteten Mails</p>
        </div>
      )}

      <div className="space-y-4">
        {mails.map(mail => (
          <div key={mail.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm">
            <div onClick={() => setExpandedMail(expandedMail === mail.id ? null : mail.id)}
              className="p-4 cursor-pointer hover:bg-gray-50 transition-colors rounded-t-2xl">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{mail.subject}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {mail.from_name || mail.from_email}
                    {mail.from_name && mail.from_email ? ' · ' + mail.from_email : ''}
                  </p>
                </div>
                <span className="text-xs text-gray-300 shrink-0 ml-4">
                  {new Date(mail.received_at).toLocaleString('de-DE')}
                </span>
              </div>
            </div>

            {expandedMail === mail.id && (
              <div className="px-4 pb-4 border-t border-gray-50">
                <div className="text-sm text-gray-700 prose prose-sm max-w-none py-3 border-b border-gray-50 mb-4"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(mail.body || '') }} />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-medium text-gray-500">Typ *</label>
                    </div>
                    <div className="flex gap-2 mb-2">
                      <button type="button" onClick={() => setOwnerType(prev => ({ ...prev, [mail.id]: 'customer' }))}
                        className={'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ' + ((ownerType[mail.id] || 'customer') === 'customer' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-600 border-gray-200')}>
                        Kunde
                      </button>
                      <button type="button" onClick={() => setOwnerType(prev => ({ ...prev, [mail.id]: 'supplier' }))}
                        className={'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ' + (ownerType[mail.id] === 'supplier' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-600 border-gray-200')}>
                        Lieferant
                      </button>
                    </div>
                    {(ownerType[mail.id] || 'customer') === 'customer' ? (
                    <select value={selectedCustomer[mail.id] || ''}
                      onChange={e => loadContacts(mail.id, e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-50">
                      <option value="">Kunde auswaehlen...</option>
                      {customers.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    ) : (
                    <select value={selectedSupplier[mail.id] || ''}
                      onChange={e => loadSupplierContacts(mail.id, e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-50">
                      <option value="">Lieferant auswaehlen...</option>
                      {suppliers.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-medium text-gray-500">Ansprechpartner</label>
                      {(ownerType[mail.id] || 'customer') === 'customer' && selectedCustomer[mail.id] && (
                        <button onClick={() => { setNewContactMailId(mail.id); setShowNewContact(true); }}
                          className="text-xs text-primary-500 hover:text-primary-600 flex items-center gap-1">
                          <Plus size={11} /> Neu
                        </button>
                      )}
                    </div>
                    {(ownerType[mail.id] || 'customer') === 'customer' ? (
                    <select value={selectedContact[mail.id] || ''}
                      onChange={e => setSelectedContact(prev => ({ ...prev, [mail.id]: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-50"
                      disabled={!contacts[mail.id]?.length}>
                      <option value="">Kein Ansprechpartner</option>
                      {(contacts[mail.id] || []).map(c => (
                        <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                      ))}
                    </select>
                    ) : (
                    <select value={selectedSupplierContact[mail.id] || ''}
                      onChange={e => setSelectedSupplierContact(prev => ({ ...prev, [mail.id]: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-50"
                      disabled={!supplierContacts[mail.id]?.length}>
                      <option value="">Kein Ansprechpartner</option>
                      {(supplierContacts[mail.id] || []).map(sc => (
                        <option key={sc.id} value={sc.id}>{sc.first_name} {sc.last_name}</option>
                      ))}
                    </select>
                    )}
                  </div>
                  <div className="flex items-end gap-2">
                    <button onClick={() => handleConvert(mail.id)} disabled={converting === mail.id}
                      className="flex-1 flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors shadow-sm disabled:opacity-50">
                      <ArrowRight size={14} />
                      Ticket erstellen
                    </button>
                    <button onClick={() => handleDelete(mail.id)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Modal: Neuer Kunde */}
      {showNewCustomer && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900">Neuer Kunde</h3>
              <button onClick={() => setShowNewCustomer(false)} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
                <input type="text" value={customerForm.name}
                  onChange={e => setCustomerForm({...customerForm, name: e.target.value})}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Kundennummer *</label>
                <input type="text" value={customerForm.customer_number}
                  onChange={e => setCustomerForm({...customerForm, customer_number: e.target.value})}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={handleCreateCustomer}
                className="flex-1 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors">
                Anlegen
              </button>
              <button onClick={() => setShowNewCustomer(false)}
                className="flex-1 border border-gray-200 text-gray-600 text-sm font-medium py-2.5 rounded-xl hover:bg-gray-50 transition-colors">
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Neuer Ansprechpartner */}
      {showNewContact && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900">Neuer Ansprechpartner</h3>
              <button onClick={() => setShowNewContact(false)} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Vorname *</label>
                  <input type="text" value={contactForm.first_name}
                    onChange={e => setContactForm({...contactForm, first_name: e.target.value})}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nachname *</label>
                  <input type="text" value={contactForm.last_name}
                    onChange={e => setContactForm({...contactForm, last_name: e.target.value})}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">E-Mail</label>
                <input type="email" value={contactForm.email}
                  onChange={e => setContactForm({...contactForm, email: e.target.value})}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Telefon</label>
                <input type="text" value={contactForm.phone}
                  onChange={e => setContactForm({...contactForm, phone: e.target.value})}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={handleCreateContact}
                className="flex-1 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors">
                Anlegen
              </button>
              <button onClick={() => setShowNewContact(false)}
                className="flex-1 border border-gray-200 text-gray-600 text-sm font-medium py-2.5 rounded-xl hover:bg-gray-50 transition-colors">
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
